import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { usePathname, useRouter } from 'expo-router';
import { useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNeedsAction } from '@/contexts/NeedsActionContext';
import { useTabTransition } from '@/contexts/TabTransitionContext';
import { useTheme } from '@/contexts/ThemeContext';
import type { AppColors } from '@/constants/theme';
import { fontSize } from '@/constants/theme';

const TABS = [
  { route: '/',           icon: 'home'          },
  { route: '/agreements', icon: 'document-text' },
  { route: '/profile',    icon: 'person'        },
] as const;

export function TabBar() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const router   = useRouter();
  const insets   = useSafeAreaInsets();
  const pathname = usePathname();
  const { breakdown } = useNeedsAction();
  const { setDirection } = useTabTransition();

  const TAB_LABELS: Record<string, string> = {
    '/': t('home.tab_label'),
    '/agreements': t('agreements.tab_label'),
    '/profile': t('profile.title'),
  };

  return (
    <View style={[styles.bar, { paddingBottom: Math.max(insets.bottom, 8) }]}>
      {TABS.map((tab) => {
        const active = pathname === tab.route;
        const showBadge = tab.route === '/agreements' && breakdown.agreements > 0;
        return (
          <Pressable
            key={tab.route}
            style={styles.btn}
            onPress={() => {
              if (!active) {
                const currentIdx = TABS.findIndex((t) => t.route === pathname || (pathname === '/' && t.route === '/'));
                const nextIdx = TABS.findIndex((t) => t.route === tab.route);
                setDirection(nextIdx > currentIdx ? 'right' : 'left');
                router.replace(tab.route);
              }
            }}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
          >
            <View style={styles.iconWrap}>
              {active && <View style={styles.dot} />}
              <Ionicons
                name={(active ? tab.icon : `${tab.icon}-outline`) as keyof typeof Ionicons.glyphMap}
                size={23}
                color={active ? colors.primary : colors.textMuted}
              />
              {showBadge && (
                <View style={styles.badge}>
                  <Text style={styles.badgeText}>{breakdown.agreements > 9 ? '9+' : breakdown.agreements}</Text>
                </View>
              )}
            </View>
            <Text style={[styles.label, active && styles.labelActive]}>
              {TAB_LABELS[tab.route]}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    bar: {
      flexDirection: 'row',
      backgroundColor: colors.surface,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.border,
      paddingTop: 8,
    },
    btn: {
      flex: 1,
      alignItems: 'center',
      gap: 3,
    },
    iconWrap: {
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
    },
    dot: {
      position: 'absolute',
      top: -6,
      width: 4,
      height: 4,
      borderRadius: 2,
      backgroundColor: colors.primary,
    },
    label: {
      color: colors.textMuted,
      fontSize: 11,
      fontWeight: '500',
    },
    labelActive: {
      color: colors.primary,
      fontWeight: '700',
    },
    badge: {
      position: 'absolute',
      top: -5,
      right: -9,
      minWidth: 16,
      height: 16,
      borderRadius: 8,
      backgroundColor: '#EF4444',
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 3,
      borderWidth: 1.5,
      borderColor: colors.surface,
    },
    badgeText: {
      color: '#FFFFFF',
      fontSize: 9,
      fontWeight: '800',
      lineHeight: 12,
    },
  });
}
